import { logger } from '../../../utils/logger'
import { getJsonSessionItem, setJsonSessionItem } from '../../../utils/session-storage'
import type { ServiceHandler } from './service-registry'
import type {
  DonorQuestionAnswers,
  SetDonorQuestionAnswerRequest,
  SetDonorQuestionAnswerResponse,
} from '@custom-embedded-code-sdk/methods/donor-questions'

export const getSessionStorageCustomQuestionsKey = (campaignId: string) =>
  `custom_questions_${campaignId}`

/**
 * SET_CUSTOM_QUESTION_ANSWER service handler
 *
 * Handles requests to set an answer for a single Custom Question.
 * Stores the answer in session storage under the key `custom_questions_{campaignId}`.
 */
export const setCustomQuestionAnswer: ServiceHandler<
  SetDonorQuestionAnswerRequest,
  SetDonorQuestionAnswerResponse
> = async ({
  iframeId,
  messageId,
  payload,
  sendSuccessResponse,
  sendErrorResponse,
  pageConfig,
}) => {
  const campaignId = pageConfig.campaignId

  try {
    if (!campaignId) {
      throw new Error('CustomEmbeddedCode:setCustomQuestionAnswer: Campaign ID not available')
    }

    // TODO: CL-51093 improve payload validation with Yup/Zod library
    if (!payload || typeof payload !== 'object') {
      sendErrorResponse(iframeId, messageId, 'Payload is expected to be an object')
      return
    }

    const { questionId, answer } = payload

    if (!questionId) {
      sendErrorResponse(iframeId, messageId, 'Question ID is required')
      return
    }

    const storageKey = getSessionStorageCustomQuestionsKey(campaignId)
    const existingAnswers = getJsonSessionItem<DonorQuestionAnswers>(storageKey) || {}

    existingAnswers[questionId] = answer
    setJsonSessionItem(storageKey, existingAnswers)

    logger(
      'info',
      'CustomEmbeddedCode:setCustomQuestionAnswer: Custom Question answer stored successfully',
      {
        context: { iframeId, messageId, campaignId, questionId, storageKey },
      },
    )

    sendSuccessResponse(iframeId, messageId, { success: true })
  } catch (error) {
    logger(
      'error',
      new Error(
        'CustomEmbeddedCode:setCustomQuestionAnswer: Failed to store Custom Question answer',
        {
          cause: error,
        },
      ),
      {
        context: { iframeId, messageId, campaignId },
      },
    )
    sendErrorResponse(iframeId, messageId, 'Unable to store Custom Question answer')
  }
}
